import axios from 'axios';
import { METADATA_URL } from './urls.mjs';
import { Property } from '../Components/ProjectConfig/types';
import {
  GpsTracker,
  GpsTrackerDto,
  LocatedAsset,
  LocatedAssetDto,
  PagiantedGpsTrackers,
  PagiantedLocatedAsset,
  PaginateGpsTrackersQuery,
  PaginateLocatedAssetQuery,
  PaginatePortableAssetQuery,
  PortableAsset,
  ProjectPropertiesDto,
} from './types/metadata';
import { Asset, AssetFilter, ToolType } from '../Components/Map/types';
import { AssetEntity } from '../util/enums';

export const getGeomobyProperties = async ({
  clientId,
  projectId,
  Authorization,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
}) => {
  return (
    await axios.get<Property[]>(`${METADATA_URL}/${clientId}/${projectId}/properties/geomoby`, {
      headers: { Authorization },
    })
  ).data;
};

export const getProjectProperties = async ({
  clientId,
  projectId,
  Authorization,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
}) => {
  return (
    await axios.get<Property[]>(`${METADATA_URL}/${clientId}/${projectId}/properties/project`, {
      headers: { Authorization },
    })
  ).data;
};

export const createProjectProperties = async ({
  clientId,
  projectId,
  Authorization,
  params,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  params: ProjectPropertiesDto;
}) => {
  return (
    await axios.post<Property>(
      `${METADATA_URL}/${clientId}/${projectId}/properties/project`,
      params,
      { headers: { Authorization } },
    )
  ).data;
};

export const updateProjectProperties = async ({
  clientId,
  projectId,
  Authorization,
  id,
  params,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  id: string;
  params: ProjectPropertiesDto;
}) => {
  return (
    await axios.patch<Property>(
      `${METADATA_URL}/${clientId}/${projectId}/properties/project/${id}`,
      params,
      { headers: { Authorization } },
    )
  ).data;
};

export const deleteProjectProperties = async ({
  clientId,
  projectId,
  Authorization,
  id,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  id: string;
}) => {
  return (
    await axios.delete<Property>(
      `${METADATA_URL}/${clientId}/${projectId}/properties/project/${id}`,
      { headers: { Authorization } },
    )
  ).data;
};

export const getGpsTracker = async ({
  clientId,
  projectId,
  Authorization,
  serial,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  serial: string;
}) => {
  return (
    await axios.get<GpsTracker>(`${METADATA_URL}/${clientId}/${projectId}/gps-tracker/${serial}`, {
      headers: { Authorization },
    })
  ).data;
};

export const getGpsTrackers = async ({
  clientId,
  projectId,
  Authorization,
  query,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  query: PaginateGpsTrackersQuery;
}) => {
  const queryParams = new URLSearchParams(
    Object.entries({
      ...(query ?? {}),
      perPage: query?.perPage ?? 50,
    })
      .map(([key, val]) => [key, val?.toString() || undefined])
      .filter((entry): entry is [string, string] => entry[1] !== undefined),
  );
  return (
    await axios.get<PagiantedGpsTrackers>(
      `${METADATA_URL}/${clientId}/${projectId}/gps-tracker?${queryParams.toString()}`,
      { headers: { Authorization } },
    )
  ).data;
};

export const createGpsTracker = async ({
  clientId,
  projectId,
  Authorization,
  gpsTracker,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  gpsTracker: GpsTrackerDto;
}) => {
  return (
    await axios.post<GpsTracker>(
      `${METADATA_URL}/${clientId}/${projectId}/gps-tracker`,
      gpsTracker,
      {
        headers: { Authorization },
      },
    )
  ).data;
};

export const updateGpsTracker = async ({
  clientId,
  projectId,
  Authorization,
  gpsTracker,
  serialToUpdate,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  gpsTracker: GpsTrackerDto;
  serialToUpdate: string;
}) => {
  return (
    await axios.patch<GpsTracker>(
      `${METADATA_URL}/${clientId}/${projectId}/gps-tracker/${serialToUpdate}`,
      gpsTracker,
      { headers: { Authorization } },
    )
  ).data;
};

export const deleteGpsTracker = async ({
  clientId,
  projectId,
  Authorization,
  serial,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  serial: string;
}) => {
  await axios.delete(`${METADATA_URL}/${clientId}/${projectId}/gps-tracker/${serial}`, {
    headers: { Authorization },
  });
};

export const getPortableAsset = async ({
  clientId,
  projectId,
  Authorization,
  uuid,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  uuid: string;
}) => {
  return (
    await axios.get<PortableAsset>(
      `${METADATA_URL}/${clientId}/${projectId}/portableasset/${uuid}`,
      { headers: { Authorization } },
    )
  ).data;
};

export const createPortableAsset = async ({
  clientId,
  projectId,
  Authorization,
  portableAsset,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  portableAsset: Asset;
}) => {
  return (
    await axios.post<PortableAsset>(
      `${METADATA_URL}/${clientId}/${projectId}/portableasset`,
      portableAsset,
      { headers: { Authorization } },
    )
  ).data;
};

export const updatePortableAsset = async ({
  clientId,
  projectId,
  Authorization,
  portableAsset,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  portableAsset: Asset & { uuid: string };
}) => {
  return (
    await axios.patch<PortableAsset>(
      `${METADATA_URL}/${clientId}/${projectId}/portableasset/${portableAsset.uuid}`,
      portableAsset,
      { headers: { Authorization } },
    )
  ).data;
};

export const deletePortableAsset = async ({
  clientId,
  projectId,
  Authorization,
  uuid,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  uuid: string;
}) => {
  return (
    await axios.delete<PortableAsset>(
      `${METADATA_URL}/${clientId}/${projectId}/portableasset/${uuid}`,
      { headers: { Authorization } },
    )
  ).data;
};

export const searchPortableAssets = async ({
  clientId,
  projectId,
  Authorization,
  params,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  params: {
    id?: string;
    type: AssetEntity.Beacon | AssetEntity.Device | AssetEntity.Tool;
    label?: string;
  };
}) => {
  return (
    await axios.post<PortableAsset[]>(
      `${METADATA_URL}/${clientId}/${projectId}/portableasset/search`,
      params,
      { headers: { Authorization } },
    )
  ).data;
};

export const getPortableAssets = async ({
  clientId,
  projectId,
  Authorization,
  perPage,
  page,
  filter,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  perPage?: number;
  page: number;
  filter: AssetFilter | undefined;
}) => {
  let filters = `?perPage=${perPage ?? 5}`;
  Object.entries(filter ?? {}).map(([key, value]) => {
    if (key !== undefined && value !== undefined) {
      filters = filters.concat(
        `&${key}=${typeof value === 'object' ? value.id : value.toString()}`,
      );
    }
  });
  return (
    await axios.get<{ portableAssets: (Asset & { uuid: string })[]; count: number }>(
      `${METADATA_URL}/${clientId}/${projectId}/portableasset/paginate/${page}${filters}`,
      { headers: { Authorization } },
    )
  ).data;
};

export const getPortableAssetTools = async ({
  clientId,
  projectId,
  Authorization,
  query,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  query: PaginatePortableAssetQuery | undefined;
}) => {
  const queryParams = new URLSearchParams(
    Object.entries({
      ...(query ?? {}),
      perPage: query?.perPage ?? 50,
    })
      .map(([key, val]) => [key, val?.toString() || undefined])
      .filter((entry): entry is [string, string] => entry[1] !== undefined && entry[0] !== 'page'),
  ).toString();
  return (
    await axios.get<{ tools: PortableAsset[]; count: number }>(
      `${METADATA_URL}/${clientId}/${projectId}/portableasset/tool/paginate/${
        query?.page ?? 1
      }?${queryParams}`,
      { headers: { Authorization } },
    )
  ).data;
};

export const bulkUpsertTools = async ({
  clientId,
  projectId,
  Authorization,
  uploads,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  uploads: {
    tool: string;
    id: string;
    label: string | null;
    type: 'tool';
  }[];
}) => {
  return (
    await axios.post<PortableAsset[]>(
      `${METADATA_URL}/${clientId}/${projectId}/portableasset/tool/bulk`,
      uploads,
      { headers: { Authorization } },
    )
  ).data;
};

export const createToolType = async ({
  clientId,
  projectId,
  Authorization,
  toolTypeName,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  toolTypeName: string;
}) => {
  return (
    await axios.post<ToolType>(
      `${METADATA_URL}/${clientId}/${projectId}/portableasset/tool`,
      { tool: toolTypeName?.trim() },
      { headers: { Authorization } },
    )
  ).data;
};

export const updateToolType = async ({
  clientId,
  projectId,
  Authorization,
  toolType,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  toolType: ToolType;
}) => {
  return (
    await axios.patch<ToolType>(
      `${METADATA_URL}/${clientId}/${projectId}/portableasset/tool/${toolType?.id}`,
      { tool: toolType?.tool?.trim() },
      { headers: { Authorization } },
    )
  ).data;
};

export const deleteToolType = async ({
  clientId,
  projectId,
  Authorization,
  id,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  id: string;
}) => {
  return (
    await axios.delete(`${METADATA_URL}/${clientId}/${projectId}/portableasset/tool/${id}`, {
      headers: { Authorization },
    })
  ).data;
};

export const getToolTypes = async ({
  clientId,
  projectId,
  Authorization,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
}) => {
  return (
    await axios.get<ToolType[]>(`${METADATA_URL}/${clientId}/${projectId}/portableasset/tool/all`, {
      headers: { Authorization },
    })
  ).data;
};

export const getLocatedAsset = async ({
  clientId,
  projectId,
  Authorization,
  id,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  id: string;
}) => {
  return (
    await axios.get<LocatedAsset>(`${METADATA_URL}/${clientId}/${projectId}/locatedasset/${id}`, {
      headers: { Authorization },
    })
  ).data;
};

export const getLocatedAssets = async ({
  clientId,
  projectId,
  Authorization,
  query,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  query: PaginateLocatedAssetQuery | undefined;
}) => {
  const queryParams = new URLSearchParams(
    Object.entries({
      ...(query ?? {}),
      perPage: query?.perPage ?? 50,
    })
      .map(([key, val]) => [key, val?.toString() || undefined])
      .filter((entry): entry is [string, string] => entry[1] !== undefined),
  ).toString();
  return (
    await axios.get<PagiantedLocatedAsset>(
      `${METADATA_URL}/${clientId}/${projectId}/locatedasset/paginate?${queryParams}&surface=true`,
      { headers: { Authorization } },
    )
  ).data;
};

export const createLocatedAsset = async ({
  clientId,
  projectId,
  Authorization,
  locatedAsset,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  locatedAsset: LocatedAssetDto;
}) => {
  return (
    await axios.post<LocatedAssetDto>(
      `${METADATA_URL}/${clientId}/${projectId}/locatedAsset`,
      { ...locatedAsset, surface: true },
      { headers: { Authorization } },
    )
  ).data;
};

export const updateLocatedAsset = async ({
  clientId,
  projectId,
  Authorization,
  locatedAsset,
  uuid,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  locatedAsset: LocatedAssetDto;
  uuid: string;
}) => {
  return (
    await axios.patch<LocatedAssetDto>(
      `${METADATA_URL}/${clientId}/${projectId}/locatedasset/${uuid}`,
      locatedAsset,
      { headers: { Authorization } },
    )
  ).data;
};

export const deleteLocatedAsset = async ({
  clientId,
  projectId,
  Authorization,
  uuid,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  uuid: string;
}) => {
  await axios.delete(`${METADATA_URL}/${clientId}/${projectId}/locatedasset/${uuid}`, {
    headers: { Authorization },
  });
};
